import { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../store/store";
import { ActionCall, Alert, Button } from "cdhs-ui-library";
import { auth } from "../../store/user/userSlice";
import { useTranslations } from "../../contexts/LanguageContext";
import { submitSurvey } from "../../store/survey/surveySlice";
import { unwrapResult } from "@reduxjs/toolkit";
import ProfileForm from "../../components/ProfileForm/ProfileForm";
import styles from "./profile.module.scss";
import { useNavigate } from "react-router";
import { ROUTES } from "../../config/routes";
import { IStatus } from "../../types";
import { ErrorSuccessMessage } from "../../components/ErrorSuccessMessage/ErrorSuccessMessage";
import ContactUsButton from "../../components/ButtonContactUs/ButtonContactUs";
import Overlay from "../../components/Overlay/Overlay";


function Profile() {
  // Hooks for state management and Redux interactions.
  const t = useTranslations();
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.user.user);

  // Local state for UI interactions and component data.
  const [displayContactForm, setDisplayContactForm] = useState<boolean>(false);
  const [disableW9Button, setDisableW9Button] = useState<boolean>(false);
  const [surveyCompletedAlert, setSurveyCompletedAlert] = useState<boolean>(false)
  const [requestStatus, setRequestStatus] = useState<IStatus>({
    loading: false,
    error: undefined,
    success: undefined,
  });
  const navigate = useNavigate();
  useEffect(() => {
    if (user?.SurveyStatus == "C") {
      setSurveyCompletedAlert(true)
    } else {
      return
    }
  }, [user?.SurveyStatus])

  const handleSurveyStatusAlert = () => {
    setSurveyCompletedAlert(prevState => !prevState)
    navigate(ROUTES.USER_PROFILE)
  }

  /**
   * Asynchronous function to handle the "Request W9" button click.
   *
   * It dispatches the `submitSurvey` thunk and manages the local
   * component state for loading, success, and error messages.
   */
  async function sendW9Email() {
    // Reset status and show loading indicator at the start of the request.
    setRequestStatus({ loading: true, error: undefined, success: undefined });

    try {
      const w9Payload = {
        SessionId: user?.SessionId,
        ClientId: user?.ClientId,
        Decline: false,
        ResetSurvey: false,
        SendW9: true,
      };

      // Dispatch the thunk and use unwrapResult to handle success and failure.
      const actionResult = await dispatch(submitSurvey({ ...w9Payload }));
      unwrapResult(actionResult);

      // On a successful response, update the state with a success message and disable the button.
      setRequestStatus({
        loading: false,
        success: t("contactPage_successMsg", "Someone will contact you shortly in regards to your W9 request"),
      });
      setDisableW9Button(true);
    } catch (error: any) {

      // On a rejected thunk, update the state with the error message.
      setRequestStatus({
        loading: false,
        error: error?.Message || t("contactPage_errorMsg", "An unknown error occurred. Please try again."),
      });
    }
  }

  return (
    <>
      <header className={styles.header}>
        <h1 id="profile-page-main-heading">{t('profilePage_mainTitle', 'My NYTD Profile')}</h1>
      </header>
    <Overlay>




      {!displayContactForm && (
        <div className={styles.buttonGroup}>
          <ErrorSuccessMessage error={requestStatus.error} success={requestStatus.success} loading={requestStatus.loading} />
          {/* Update contact info button */}
          <Button
            caption={t('profilePage_updateContactInfoButton', 'Update contact info')}
            variant="NYTD"
            option="info"
            onClick={() => setDisplayContactForm(true)}
          />
          {/* Request W9 button */}
          <Button
            disabled={disableW9Button || requestStatus.loading}
            caption={t('profilePage_requestW9Button', 'Request W9')}
            variant="NYTD"
            option={(disableW9Button || requestStatus.loading) ? "" : "info"}
            onClick={sendW9Email}
          />
          {/* Logout button */}
          <Button
            caption={t('logoutButton', 'Logout')}
            variant="primary"
            mode="light"
            onClick={() => dispatch(auth({ ...user, LoginAttempt: false }))}
          />
        </div>
      )}


      {displayContactForm && <ProfileForm setDisplayContactForm={setDisplayContactForm} />}

      <Alert
        open={surveyCompletedAlert}
        onClose={() => handleSurveyStatusAlert()}
        type="caution"
      >
        <div className={styles['modal']}>
          <h2 id="completedSurvey_title-id">{t('completedSurvey_title', 'You’ve already completed this survey.')}</h2>
          <p>{t('completedSurvey_text', 'Our records indicate that you have already completed and submitted this survey. Please contact us if you think this is a mistake.')}</p>
          <ContactUsButton onClick={() => {
            handleSurveyStatusAlert();
          }} option="caution" />

          <ActionCall label="Close" onClick={() => { setSurveyCompletedAlert(prevState => !prevState); navigate(ROUTES.USER_PROFILE) }}>

          </ActionCall>
        </div>
      </Alert>
    </Overlay>
    </>
  );
}

export default Profile;